import { useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import { Map } from 'immutable';
import { omit } from 'lodash';

import { showEmbeddedUpgradePlanModal } from 'actions/modals';

import * as sessionSelectors from 'selectors/session';

import { showUpgradeModal } from 'util/upgrades';

interface Props {
  feature: string;
  report?: string;
  paywallVersion?: string;
  locationProperty?: string;
  serverKey?: string;
  onShowUpgradeModal?: (options: { focusedFeature: string }) => void;
  featureMinTiers: Map<string, any>;
  reportMinTiers: Map<string, any>;
}

const withUpgradeModal = (WrappedComponent: React.ComponentType<any>) => {
  const ComponentWithUpgradeModal: React.FC<Props> = props => {
    const {
      feature,
      report,
      paywallVersion,
      locationProperty,
      serverKey,
      featureMinTiers,
      reportMinTiers,
    } = props;

    const isEmbedded = useIsEmbedded();
    const dispatch = useDispatch();

    const minTier = useCallback(() => {
      if (report) {
        return reportMinTiers.get(report);
      }
      return featureMinTiers.get(feature);
    }, [feature, report, featureMinTiers, reportMinTiers]);

    const handleShowUpgradeModal = useCallback(
      ({ focusedFeature }: any) => {
        if (isEmbedded) {
          return dispatch(showEmbeddedUpgradePlanModal(minTier()));
        }
        return showUpgradeModal({
          minTier: minTier(),
          feature: paywallVersion || feature,
          locationProperty,
          serverKey,
          mppFocusViewProps: { focusedFeature },
        });
      },
      [
        feature,
        isEmbedded,
        locationProperty,
        paywallVersion,
        serverKey,
        dispatch,
        minTier,
      ]
    );

    const componentProps = {
      onShowUpgradeModal: handleShowUpgradeModal,
      ...omit(props, ['featureMinTiers', 'reportMinTiers']),
    };

    return <WrappedComponent {...componentProps} />;
  };

  return connect((state: any) => ({
    featureMinTiers: sessionSelectors.getFeatureMinTiers(state),
    reportMinTiers: sessionSelectors.getReportMinTiers(state),
  }))(ComponentWithUpgradeModal);
};

export default withUpgradeModal;
